import { memo, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { MarkerIcon } from '@client/images/icons/MarkerIcon';
import { buildURIFromParams } from '@sda/utils/url';
import { getLocation } from '@sda/utils/location';
import { useIsMobile } from '@client/hooks/dom.hooks';
import type { UnknownLocation } from '@sda/utils/location/interfaces';

const useStyles = makeStyles({ name: 'HostMap' })((theme) => ({
  root: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: theme.radii.large,
    aspectRatio: '16 / 9',

    '&::before': {
      content: '""',
      display: 'block',
      paddingTop: '56.25%',
    },

    [theme.breakpoints.up('md')]: {
      aspectRatio: '25 / 9',

      '&::before': {
        paddingTop: '36%',
      },
    },
  },
  image: {
    width: '100%',
    position: 'absolute',
    top: '-100%',
    bottom: '-100%',
    right: 0,
    left: 0,
    margin: 'auto',
  },
  markerIcon: {
    fontSize: '24px',
    overflow: 'visible',
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    top: 'calc(50% - 12px)',
    left: '50%',
    pointerEvents: 'none',
    touchAction: 'none',
    zIndex: 1,
  },
}));

interface HostMapProps {
  readonly location: UnknownLocation;
  readonly zoom?: number;
}

const style = 'style=feature:administrative%7Celement:labels.text.fill%7Ccolor:0x444444&style=feature:landscape%7Celement:all%7Ccolor:0xf2f2f2&style=feature:landscape.natural%7Celement:all%7Cvisibility:on%7Ccolor:0xd8e9d9&style=feature:poi%7Celement:all%7Cvisibility:off&style=feature:road%7Celement:all%7Csaturation:-100%7Clightness:45&style=feature:road.highway%7Celement:all%7Cvisibility:simplified&style=feature:road.arterial%7Celement:labels.icon%7Cvisibility:off&style=feature:transit%7Celement:all%7Cvisibility:off&style=feature:water%7Celement:all%7Ccolor:0xc9daf8%7Cvisibility:on';

export const HostMap = memo<HostMapProps>(({ location, zoom }) => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  const mapUrl = useMemo(() => {
    const formattedLocation = getLocation(location);

    let zoomLevel = zoom;

    if (!zoomLevel) {
      zoomLevel = isMobile ? 10 : 9;
    }

    const url = buildURIFromParams('https://maps.googleapis.com/maps/api/staticmap', {
      scale: 2,
      center: `${formattedLocation.lat},${formattedLocation.lng}`,
      zoom: zoomLevel,
      size: '800x450',
      key: process.env.GOOGLE_MAPS_API_KEY,
    });

    return `${url}&${style}`;
  }, [location, isMobile, zoom]);

  return (
    <div className={classes.root}>
      <img
        className={classes.image}
        src={mapUrl}
        alt="Map of host area"
      />

      <MarkerIcon
        className={classes.markerIcon}
      />
    </div>
  );
});
